import * as Schema from "effect/Schema";

import { parseFilesystemMemberReference } from "./contextSelection.ts";
import { TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * The five typed source groups of one room universe (O10, CX14). This
 * union is CLOSED: every governable context member belongs to exactly one
 * group, and no sixth untyped bucket exists. Ephemera — pasted text,
 * screenshots, web results — are not governable until materialized into
 * `world-files` or `world-vfs` with a typed member identity (CX28);
 * conversation history is Agent-session state, never a room member.
 */
export const RoomSourceGroup = Schema.Literals([
  "root-codebase",
  "world-convex",
  "world-files",
  "world-vfs",
  "world-r2",
]);
export type RoomSourceGroup = typeof RoomSourceGroup.Type;

export const ROOM_SOURCE_GROUPS: ReadonlyArray<RoomSourceGroup> = [
  "root-codebase",
  "world-convex",
  "world-files",
  "world-vfs",
  "world-r2",
] as const;

/**
 * One member's stable identity: `<group>:<key>`. Keys are portable — a
 * physical source reference (or a legacy relative file path) for filesystem
 * groups, a stable record key for World groups. Absolute machine paths never enter a member id
 * (O12): the device-local roots live in each machine's own settings, and the
 * id names the member the same way on every device.
 */
export const ROOM_MEMBER_ID_SEPARATOR = ":";

const ABSOLUTE_PATH = /^(\/|[A-Za-z]:[\\/]|\\\\|~)/;
const PARENT_ESCAPE = /(^|[\\/])\.\.([\\/]|$)/;

export type RoomMemberIdParts = {
  readonly group: RoomSourceGroup;
  readonly key: string;
};

export type RoomMemberIdRefusal = {
  readonly _tag: "Refused";
  readonly reason: "unknown-group" | "empty-key" | "absolute-path" | "parent-escape";
};

/** Compose a member id from validated parts. Throws only on programmer error. */
export const roomMemberId = (group: RoomSourceGroup, key: string): string => {
  const parsed = parseRoomMemberId(`${group}${ROOM_MEMBER_ID_SEPARATOR}${key}`);
  if ("_tag" in parsed) {
    throw new Error(`roomMemberId refused: ${parsed.reason} for '${group}:${key}'`);
  }
  return `${group}${ROOM_MEMBER_ID_SEPARATOR}${key}`;
};

/**
 * Parse and validate one member id. A typed refusal, never a guess: an
 * unknown group is the sixth-bucket defect (O10); an absolute or
 * parent-escaping path is the machine-path defect (O12).
 */
export const parseRoomMemberId = (id: string): RoomMemberIdParts | RoomMemberIdRefusal => {
  const separator = id.indexOf(ROOM_MEMBER_ID_SEPARATOR);
  const group = separator === -1 ? id : id.slice(0, separator);
  if (!(ROOM_SOURCE_GROUPS as ReadonlyArray<string>).includes(group)) {
    return { _tag: "Refused", reason: "unknown-group" };
  }
  const key = separator === -1 ? "" : id.slice(separator + 1);
  if (key.trim().length === 0) return { _tag: "Refused", reason: "empty-key" };
  if (ABSOLUTE_PATH.test(key) && !parseFilesystemMemberReference(id))
    return { _tag: "Refused", reason: "absolute-path" };
  if (PARENT_ESCAPE.test(key)) return { _tag: "Refused", reason: "parent-escape" };
  return { group: group as RoomSourceGroup, key };
};

/**
 * The contractual world-vfs member grammar (CB16, principal ruling
 * 2026-08-27): the Convex revision tier's path prefixes are member
 * identity, not incidental convention — the door mints them
 * (`backend/convex/mcpVfs.js`) and a display fold may legally derive from
 * them. One identity source; a hand-built prefix anywhere else is a
 * defect. An unrecognized world-vfs prefix is a typed null, never a guess:
 * such a member stays reachable in the whole-universe view and joins a
 * kind only when the grammar names it.
 */
export const WORLD_VFS_CANVAS_PREFIX = "canvas/";
export const WORLD_VFS_BOARD_COMPONENT_PREFIX = "board-components/";
// CB18: the blueprints tier — Idea-compiled experience lineages, never a
// component manifest wearing the Blueprint name.
export const WORLD_VFS_BLUEPRINT_PREFIX = "blueprints/";

export type WorldVfsMemberKind = "canvas" | "blueprint" | "board-component";

export const worldVfsMemberKind = (memberId: string): WorldVfsMemberKind | null => {
  const parsed = parseRoomMemberId(memberId);
  if ("_tag" in parsed || parsed.group !== "world-vfs") return null;
  if (
    parsed.key.startsWith("library/component/") ||
    /^component\/[a-zA-Z0-9_-]{1,128}@[1-9][0-9]{0,9}$/.test(parsed.key)
  )
    return "board-component";
  if (parsed.key.startsWith("library/blueprint/")) return "blueprint";
  return parsed.key.startsWith(WORLD_VFS_CANVAS_PREFIX)
    ? "canvas"
    : parsed.key.startsWith(WORLD_VFS_BLUEPRINT_PREFIX)
      ? "blueprint"
      : parsed.key.startsWith(WORLD_VFS_BOARD_COMPONENT_PREFIX)
        ? "board-component"
        : null;
};

/** One catalog member as the rail renders it. Identity, not content. */
export const CanvasContentType = Schema.Literals([
  "canvas",
  "idea",
  "web-page",
  "public-invitation",
]);
export type CanvasContentType = typeof CanvasContentType.Type;
export const CANVAS_TYPE_LABELS: Readonly<Record<CanvasContentType, string>> = {
  canvas: "Canvas",
  idea: "Idea",
  "web-page": "Web Page",
  "public-invitation": "Public Invitation",
};
export const isCanvasContentType = (value: unknown): value is CanvasContentType =>
  typeof value === "string" && Object.hasOwn(CANVAS_TYPE_LABELS, value);

export const RoomMember = Schema.Struct({
  id: TrimmedNonEmptyString,
  group: RoomSourceGroup,
  label: TrimmedNonEmptyString,
  contentType: Schema.optional(CanvasContentType),
  kind: Schema.optional(Schema.Literals(["member", "folder"])),
  ancestors: Schema.optional(Schema.Array(Schema.String)),
});
export type RoomMember = typeof RoomMember.Type;

export type RoomUniverseRefusal = {
  readonly _tag: "Refused";
  readonly memberId: string;
  readonly reason: RoomMemberIdRefusal["reason"] | "group-mismatch" | "duplicate-member";
};

/**
 * Assemble one room universe from per-group member lists. Every member id
 * must parse into its declared group; one refusal refuses the whole assembly
 * with the offending id named — an untyped or mistyped member never enters
 * the catalog silently (O10).
 */
export const assembleRoomUniverse = (
  membersByGroup: Readonly<Partial<Record<RoomSourceGroup, ReadonlyArray<RoomMember>>>>,
): ReadonlyArray<RoomMember> | RoomUniverseRefusal => {
  const out: RoomMember[] = [];
  const seen = new Set<string>();
  for (const group of ROOM_SOURCE_GROUPS) {
    for (const member of membersByGroup[group] ?? []) {
      const parsed = parseRoomMemberId(member.id);
      if ("_tag" in parsed) {
        return { _tag: "Refused", memberId: member.id, reason: parsed.reason };
      }
      if (parsed.group !== group || member.group !== group) {
        return { _tag: "Refused", memberId: member.id, reason: "group-mismatch" };
      }
      if (seen.has(member.id)) {
        return { _tag: "Refused", memberId: member.id, reason: "duplicate-member" };
      }
      seen.add(member.id);
      out.push(member);
    }
  }
  return out;
};
